import {
    DEFAULT_TABLE_CAPACITY,
    SeatingGroupType,
    UNSEATED_GROUP_ID,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import {
    SeatingTicket,
    TransformedEventSeating,
    TransformedSeatingGroup,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';
import { indexToSeatNumber } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/utils/helpers';
import { SeatingGroup, Ticket, TicketGroup } from 'types/domain';

interface ApiLayoutResponse {
    seating_groups: SeatingGroup[];
    unseated_tickets: Ticket[];
}

interface TableSeatingState {
    tickets: Record<string, SeatingTicket>;
    seatingGroups: Record<string, TransformedSeatingGroup>;
    ticketGroups: Record<string, TicketGroup>;
    originalTickets: Record<string, SeatingTicket>;
    deletedSeatingGroupIds: Set<string>;
}

const formatApiData = (layoutData: ApiLayoutResponse): TransformedEventSeating => {
    const tickets: Record<string, SeatingTicket> = {};
    const seatingGroups: Record<string, TransformedSeatingGroup> = {};
    const ticketGroups: Record<string, TicketGroup> = {};

    layoutData.seating_groups.forEach(group => {
        const capacity =
            group.capacity !== null && group.capacity != undefined
                ? parseInt(group.capacity)
                : DEFAULT_TABLE_CAPACITY;

        seatingGroups[group.id.toString()] = {
            id: group.id.toString(),
            name: group.name || `Table ${group.number}`,
            capacity,
            number: group.number,
            locked: group.locked,
            type: SeatingGroupType.TABLE,
        };

        if (group.tickets) {
            group.tickets.forEach(ticket => {
                tickets[ticket.id.toString()] = {
                    id: ticket.id.toString(),
                    first_name: ticket.first_name,
                    last_name: ticket.last_name,
                    title: ticket.title,
                    ticket_group_id: ticket.ticket_group?.id?.toString() || null,
                    seating_group_id: group.id.toString(),
                    seat_number: ticket.seat_number || null,
                };
                if (ticket.ticket_group && !ticketGroups[ticket.ticket_group.id.toString()]) {
                    ticketGroups[ticket.ticket_group.id.toString()] = {
                        ...ticket.ticket_group,
                        tickets: null,
                    } as TicketGroup;
                }
            });
        }
    });

    if (!seatingGroups[UNSEATED_GROUP_ID]) {
        seatingGroups[UNSEATED_GROUP_ID] = {
            id: UNSEATED_GROUP_ID,
            name: 'Unseated',
            capacity: null,
            number: 0,
            locked: false,
            type: SeatingGroupType.UNSEATED,
        };
    }

    layoutData.unseated_tickets.forEach((ticket, index) => {
        tickets[ticket.id.toString()] = {
            id: ticket.id.toString(),
            first_name: ticket.first_name,
            last_name: ticket.last_name,
            title: ticket.title,
            ticket_group_id: ticket.ticket_group?.id?.toString() || null,
            seating_group_id: UNSEATED_GROUP_ID,
            seat_number: indexToSeatNumber(index),
        };
        if (ticket.ticket_group && !ticketGroups[ticket.ticket_group.id.toString()]) {
            ticketGroups[ticket.ticket_group.id.toString()] = {
                ...ticket.ticket_group,
                tickets: null,
            } as TicketGroup;
        }
    });

    return { tickets, seatingGroups, ticketGroups };
};

interface AssignSeatingParams {
    seating_groups: {
        id?: number;
        capacity: number;
        number?: number;
        name: string;
        allowed_item_ids?: number[];
        map_data?: object;
        locked?: boolean;
        tickets: {
            id: number;
            seat_number: number;
        }[];
    }[];
    deleted_seating_groups: number[];
    unseated_tickets: number[];
}

type ToRequestState = Pick<
    TableSeatingState,
    'tickets' | 'seatingGroups' | 'deletedSeatingGroupIds'
>;

const formatSeatingPayload = (state: ToRequestState): AssignSeatingParams => {
    const seatingGroupsArray = Object.values(state.seatingGroups)
        .filter(group => group.id !== UNSEATED_GROUP_ID)
        .map(group => ({
            id: group.id.startsWith('table-') ? undefined : parseInt(group.id),
            capacity: group.capacity !== null ? group.capacity : DEFAULT_TABLE_CAPACITY,
            number: group.number,
            name: group.name,
            locked: group.locked || false,
            tickets: Object.values(state.tickets)
                .filter(ticket => ticket.seating_group_id === group.id)
                .map(ticket => ({
                    id: parseInt(ticket.id),
                    seat_number: ticket.seat_number!,
                }))
                .sort((a, b) => a.seat_number - b.seat_number),
        }));

    const deletedIds = Array.from(state.deletedSeatingGroupIds)
        .map(id => parseInt(id))
        .filter(id => !isNaN(id));

    const unseatedTicketIds = Object.values(state.tickets)
        .filter(ticket => ticket.seating_group_id === UNSEATED_GROUP_ID || !ticket.seating_group_id)
        .map(ticket => parseInt(ticket.id));

    return {
        seating_groups: seatingGroupsArray,
        deleted_seating_groups: deletedIds,
        unseated_tickets: unseatedTicketIds,
    };
};

export type { AssignSeatingParams };

export { formatApiData, formatSeatingPayload };
